import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import { useQuote } from "../context/QuoteContext";
import { clients } from "../data/clients";
import Seo from "../components/Seo";

const ease = [0.16, 1, 0.3, 1];

function ClientCard({ client, index }) {
  const clickable = Boolean(client.href);
  const inner = (
    <>
      {/* top accent line — subtle Clinixport-blue interaction */}
      <span className="absolute top-0 left-0 right-0 h-[2px] bg-[#1F6FEB] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out" />
      {/* logo / text area — visually prominent */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 min-h-[188px] bg-white">
        {client.logo ? (
          <img
            src={client.logo}
            alt={`${client.name} logo`}
            loading="lazy"
            className="max-h-28 max-w-[82%] w-auto object-contain transition-transform duration-300 ease-out group-hover:scale-[1.04]"
          />
        ) : (
          <div className="text-center transition-transform duration-300 ease-out group-hover:scale-[1.03]">
            {(client.lines || [client.name]).map((l, i) => (
              <span
                key={i}
                className={`block tracking-tight ${
                  i === 0
                    ? "text-2xl sm:text-3xl font-extrabold text-[#0A1128] group-hover:text-[#1F6FEB] transition-colors duration-300"
                    : "text-sm font-semibold text-[#64748B] mt-1.5"
                }`}
              >
                {l}
              </span>
            ))}
          </div>
        )}
      </div>
      {/* name row */}
      <div className="border-t border-[#0A1128]/10 px-5 py-4 flex items-center justify-between gap-3">
        <span className="text-sm font-bold text-[#0A1128] leading-tight group-hover:text-[#1F6FEB] transition-colors duration-300">
          {client.name}
        </span>
        {clickable && (
          <ArrowUpRight
            size={18}
            className="shrink-0 text-[#1F6FEB] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
          />
        )}
      </div>
    </>
  );

  const base =
    "group relative overflow-hidden bg-white border border-[#0A1128]/10 flex flex-col h-full will-change-transform " +
    "transition-[transform,box-shadow,border-color] duration-300 ease-out " +
    "hover:-translate-y-[5px] hover:border-[#1F6FEB]/40 hover:shadow-[0_18px_38px_-22px_rgba(10,17,40,0.4)]";

  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease, delay: Math.min(index, 8) * 0.06 }}
      data-testid={`client-card-${client.id}`}
    >
      {clickable ? (
        <a
          href={client.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`${base} cursor-pointer`}
          data-testid={`client-link-${client.id}`}
        >
          {inner}
        </a>
      ) : (
        <div className={base} data-testid={`client-static-${client.id}`}>{inner}</div>
      )}
    </motion.div>
  );
}

export default function OurClients() {
  const { openQuote } = useQuote();
  return (
    <div data-testid="clients-page" className="pt-32">
      <Seo
        title="Our Clients | Clinixport Global"
        description="Organisations across healthcare, dental, food, hospitality and specialist businesses rely on Clinixport Global for consistent quality, dependable supply and responsive service."
        path="/clients"
        keywords="Clinixport clients, healthcare supply partner, trusted medical supplier"
      />
      {/* SPLIT HERO — text + premium visual */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-16 sm:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease }}
              className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB] mb-5"
            >
              Our Clients
            </motion.p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#0A1128] leading-[1.04]">
              <MaskedLines start={0.12} lines={["Trusted by organisations", "that value dependable supply."]} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease, delay: 0.45 }}
              className="mt-7 text-lg text-[#475569] leading-relaxed max-w-xl"
            >
              At Clinixport Global, a supply partner should be more than a vendor. We work closely
              with organisations across healthcare, food, hospitality and manufacturing — helping
              them uphold the standards their people, customers and operations depend on, with
              consistent quality, dependable supply and responsive service.
            </motion.p>
          </div>

          {/* RIGHT — visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease, delay: 0.35 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-xl border border-[#0A1128]/10 aspect-[5/4] lg:aspect-[4/5] bg-[#0A1128]">
              <img
                src="/clients-hero.jpg"
                alt="Clean clinical environment with neatly arranged sterile medical and hygiene supplies"
                loading="eager"
                className="w-full h-full object-cover"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/25 via-transparent to-transparent" />
              <span className="absolute bottom-0 left-0 h-1 w-24 bg-[#1F6FEB]" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STATEMENT — deliberate transition into the showcase */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-14">
        <Reveal>
          <div className="border-t border-[#0A1128]/10 pt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-1 hidden lg:block">
              <span className="block w-10 h-[3px] bg-[#1F6FEB]" />
            </div>
            <h2 className="lg:col-span-6 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A1128] leading-[1.12]">
              Built around dependable relationships.
            </h2>
            <p className="lg:col-span-5 text-lg text-[#475569] leading-relaxed self-end">
              From healthcare and dental practices to food, hospitality and specialist businesses,
              our clients operate in environments where consistency matters.
            </p>
          </div>
        </Reveal>
      </section>

      {/* CLIENT SHOWCASE */}
      <section className="bg-white border-y border-[#0A1128]/10 py-20 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB] mb-4">Selected Partners</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A1128] max-w-2xl">
              Organisations we're proud to supply
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients.map((c, i) => (
              <ClientCard key={c.id} client={c} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A1128] text-white py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight max-w-xl leading-tight">
              Ready to join the organisations we supply?
            </h2>
            <p className="mt-4 text-white/60 max-w-lg leading-relaxed">
              Tell us what your operation needs — we'll make procurement simpler and more reliable.
            </p>
          </div>
          <button
            onClick={() => openQuote()}
            data-testid="clients-cta-quote"
            className="shrink-0 inline-flex items-center gap-2 bg-[#1F6FEB] hover:bg-[#1556B8] transition-colors text-white px-9 py-4 font-bold text-sm tracking-widest uppercase"
          >
            Request a Quote <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
}
